import React, { useMemo, useState } from 'react';
import { SearchIcon, ShoppingBagIcon } from 'lucide-react';
import { OrderProduct } from '../hooks/useNisoloOrders';
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const count = (value: number) => value.toLocaleString('en-US');
export function NisoloProductSales({
  products,
  totalUnits
}: {
  products: OrderProduct[];
  totalUnits: number;
}) {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(15);
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return products;
    return products.filter(product => product.name.toLowerCase().includes(normalized) || product.type.toLowerCase().includes(normalized));
  }, [products, query]);
  const visible = filtered.slice(0, limit);
  return <article className="mt-5 overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="nisolo-sales-heading" data-id="element-1568" data-mp="1lw0jig39hwvc">
      <div className="flex flex-col gap-3 border-b border-ink/10 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-1569" data-mp="ym8cq117u8ax">
        <div className="flex items-center gap-2" data-id="element-1570" data-mp="tphxth15dir3">
          <ShoppingBagIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1571" data-mp="17ng6q6wp8mz7" />
          <h2 id="nisolo-sales-heading" className="text-base font-semibold" data-id="element-1572" data-mp="13m4fxy239vpf">Product sales</h2>
          <span className="text-xs text-ink/50" data-id="element-1573" data-mp="13oh4wv69l6bz">{count(filtered.length)} products</span>
        </div>
        <div className="relative w-full lg:w-72" data-id="element-1574" data-mp="1cmsv4w37y23w">
          <label className="sr-only" htmlFor="nisolo-sales-search" data-id="element-1575" data-mp="1clzw6hai4gwb">Search products</label>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-1576" data-mp="1ranngbdbe4gg" />
          <input id="nisolo-sales-search" type="search" value={query} onChange={event => {
          setQuery(event.target.value);
          setLimit(15);
        }} placeholder="Search by product or type" className="w-full rounded-xl border border-ink/10 bg-cream px-10 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-[#BB5E40]/40 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/15" data-id="element-1577" data-mp="1mep6h8ubfoq2" />
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-1578" data-mp="11yzubhf9p6rb">
        <table className="w-full min-w-[880px] text-left text-xs" data-id="element-1579" data-mp="16r9eegtpbclo">
          <thead className="bg-cream/50 text-ink/50" data-id="element-1580" data-mp="16ski6gn5ckcs">
            <tr data-id="element-1581" data-mp="1br5jkje4xa2t">
              <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-1582" data-mp="1bpkngpc69j2u">Product</th>
              <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1583" data-mp="1gonrc6gf1j98">Type</th>
              <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1584" data-mp="1lotfvdre2pn3">Orders</th>
              <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1585" data-mp="1pq569li37ewc">Units sold</th>
              <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1586" data-mp="1pnsrfh7mlkni">Total sales</th>
              <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1587" data-mp="3ni9ht6sxfqxd">AUR</th>
              <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1588" data-mp="3rhs1jz90hvym">MSRP</th>
              <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-1589" data-mp="3jzk4sex66rte">% of units</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10" data-id="element-1590" data-mp="3f565i1fe00jg">
            {visible.map(product => {
            const share = totalUnits > 0 ? product.units / totalUnits * 100 : 0;
            return <tr key={product.name} data-id="element-1591" data-mp="364rgos37s3b7">
                  <th scope="row" className="px-5 py-3.5 text-left font-medium text-ink sm:px-6" data-id="element-1592" data-mp="6rph2skj2ft6">
                    <span className="block max-w-sm truncate" data-id="element-1593" data-mp="1s4hqh1g45fx">{product.name}</span>
                    <span className="mt-0.5 block text-[11px] font-normal text-ink/45" data-id="element-1594" data-mp="3tvwzng4gbbpq">
                      {product.condition}
                      {product.sizes.length > 0 && ` · ${product.sizes.length} sizes sold`}
                    </span>
                  </th>
                  <td className="px-3 py-3.5 text-ink/65" data-id="element-1595" data-mp="2iene8o1ri9ls">{product.type}</td>
                  <td className="px-3 py-3.5 text-right tabular-nums text-ink/65" data-id="element-1596" data-mp="2dgc7im6rkf7s">{count(product.orders)}</td>
                  <td className="px-3 py-3.5 text-right font-semibold tabular-nums text-ink" data-id="element-1597" data-mp="57w478n8jp9i">{count(product.units)}</td>
                  <td className="px-3 py-3.5 text-right font-semibold tabular-nums text-ink" data-id="element-1598" data-mp="3sdwxbmkw7pjc">{currency(product.sales)}</td>
                  <td className="px-3 py-3.5 text-right tabular-nums text-ink/70" data-id="element-1599" data-mp="9mdba8jnatp">{currency(product.units > 0 ? product.sales / product.units : 0)}</td>
                  <td className="px-3 py-3.5 text-right tabular-nums text-ink/55" data-id="element-1600" data-mp="o0hwjiyxoxpc">{product.msrp > 0 ? currency(product.msrp) : '—'}</td>
                  <td className="px-5 py-3.5 sm:px-6" data-id="element-1601" data-mp="ezaca4t95xe9">
                    <span className="flex min-w-28 items-center gap-2" data-id="element-1602" data-mp="eyquco3lr908">
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/8" data-id="element-1603" data-mp="jx25r39jprkk">
                        <span className="block h-full rounded-full bg-[#BB5E40]" style={{
                      width: `${Math.max(2, share)}%`
                    }} data-id="element-1604" data-mp="2zozo08ynt32q" />
                      </span>
                      <span className="w-10 text-right tabular-nums text-ink/55" data-id="element-1605" data-mp="34lzng11077kv">{share.toFixed(1)}%</span>
                    </span>
                  </td>
                </tr>;
          })}
            {visible.length === 0 && <tr data-id="element-1606" data-mp="364geu489rk69"><td colSpan={8} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-1607" data-mp="364gge8nx3s8d">No products match that search.</td></tr>}
          </tbody>
        </table>
      </div>

      {filtered.length > visible.length && <div className="border-t border-ink/10 px-5 py-3 text-center sm:px-6" data-id="element-1608" data-mp="2derd1onqjg3l">
          <button type="button" onClick={() => setLimit(current => current + 25)} className="rounded-full border border-ink/15 px-4 py-1.5 text-xs font-semibold text-ink/70 transition-colors hover:border-[#BB5E40]/45 hover:text-[#94452E]" data-id="element-1609" data-mp="28jc0d01grq8r">
            Show more products
          </button>
        </div>}
    </article>;
}